import React from 'react';
import { SectionGroup } from 'Components/Layout';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import {
  IWithWorksheetInjectedProps,
  WorksheetRecordGrid,
  WorksheetGridFieldSection,
} from 'Components/Worksheets//Core';
import { Checkbox, Field, NumericInput, TextOutput } from 'Components/Core';
import { useDispatch, useSelector } from 'react-redux';
import { valueOrDefault } from 'Lib/Any';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import deepEquals from 'Lib/Object/deepEquals';
import { prefixRecordFieldSetNames } from 'Components/Worksheets/Core/WorksheetRecordGrid/helpers';
import { getGridFieldSet as getMoistureContentGridFieldSet } from 'Components/Worksheets/MoistureContentWorksheet/BsEnIso/gridFieldSets';
import { pipe } from 'Lib/Hooks/Selectors';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { joinPaths } from 'Lib/Object/Path/shared';
import { ChangeEventWithData } from 'Components/Core/types';

/**
 * Trials can only be added/removed/excluded in groups of 2
 */
const trialGroupSize = 2;

let BsEnIsoLiquidLimitTrialsSection: React.FC<IWithWorksheetInjectedProps> = ({
  namePrefix,
  createWorksheetSelector,
}) => {
  const dispatch = useDispatch();

  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));
  const trialRecordProps = useSelector(
    pipe(createWorksheetSelector!('Trials'), valueOrDefault([] as Array<any>), (trials) =>
      trials.map((t, i) => ({
        dim: t.ExcludedTrial,
        preventRemove: i < 2,
      }))
    ),
    deepEquals
  );

  const preventTrialCreation = useSelector(
    pipe(
      createWorksheetSelector!('Trials'),
      valueOrDefault([] as Array<any>),
      (trials) => trials.filter((t) => !t.ExcludedTrial).length >= 2
    ),
    deepEquals
  );

  const excludeTrials = (i: number, exclude: boolean) => {
    [...Array(trialGroupSize).keys()].forEach((k) =>
      dispatch(
        actions.setValue({
          name: joinPaths(namePrefix, 'Trials', `${i - k}`, 'ExcludedTrial'),
          value: exclude,
          sync: k === trialGroupSize - 1,
        })
      )
    );
  };

  const disableExcludeTrial = (i: number) => trialRecordProps.length - 1 !== i;

  return (
    <SectionGroup heading="Trials">
      <WorksheetGridFieldSection name="Trials">
        <WorksheetRecordGrid
          name={'Trials'}
          orientation={'horizontal'}
          recordProps={{
            width: 130,
            preventCreate: preventTrialCreation,
            preventRemove: (i) => trialRecordProps[i]?.preventRemove,
            dim: (i) => trialRecordProps[i]?.dim,
            enforceAtLeastOneRecord: true,
            removeConfirm: {
              confirm: true,
              confirmTitle: 'Trial Deletion',
              confirmText: 'Are you sure you want to delete the selected trial?',
              confirmButtonText: 'Delete Trial',
            },
            recordGroupingSize: trialGroupSize,
          }}
          fieldSet={[
            {
              name: 'Penetrations',
              recordProps: {
                enforceAtLeastOneRecord: false,
                preventRemove: (i) => i < 2,
                preventCreate: (count) => count === 0,
                removeConfirm: {
                  confirm: true,
                  confirmTitle: 'Delete Penetration',
                  confirmText: 'Are you sure you want to delete the penetration reading? Data cannot be recovered.',
                  confirmButtonText: 'Delete',
                },
              },
              fields: [
                {
                  name: 'InitialPenetration',
                  component: <Field input={<NumericInput />} />,
                  label: (i) => `Initial Penetration ${i + 1} (mm)`,
                },
                {
                  name: 'FinalPenetration',
                  component: <Field input={<NumericInput />} />,
                  label: (i) => `Final Penetration ${i + 1} (mm)`,
                },
                {
                  name: 'Penetration',
                  component: <Field input={<TextOutput />} />,
                  label: (i) => `Penetration ${i + 1} (mm)`,
                },
              ],
            },
            {
              name: 'Penetration',
              component: (i: number) => <Field input={<TextOutput />} value={(i % trialGroupSize) + 1} />,
            },
            ...prefixRecordFieldSetNames('MoistureDetermination', getMoistureContentGridFieldSet(dryMassEntryOption)),
            {
              name: 'ExcludedTrial',
              component: (i: number) =>
                i % trialGroupSize !== 0 ? (
                  <Field
                    input={<Checkbox />}
                    onChange={(e: ChangeEventWithData) => excludeTrials(i, e?.data?.value)}
                    disabled={disableExcludeTrial(i)}
                  />
                ) : (
                  <></>
                ),
            },
          ]}
        />
      </WorksheetGridFieldSection>
    </SectionGroup>
  );
};

BsEnIsoLiquidLimitTrialsSection = withInjectedProps(BsEnIsoLiquidLimitTrialsSection);

export { BsEnIsoLiquidLimitTrialsSection };
export default BsEnIsoLiquidLimitTrialsSection;
